import { CircleArrowUpIcon, CircleXIcon, Loader2Icon } from 'lucide-react';

import type { ManagedCustomDashboardTab } from '@/lib/api/custom-dashboard';
import { InlineLinkButton, StatusChip, StatusNote } from './TabStatusParts';

export interface TabPublishStatusProps {
  tab: ManagedCustomDashboardTab;
  /** 公開前の revision をプレビューする。 */
  onPreviewRequest: (revision: number) => void;
  onHistoryRequest: () => void;
}

/**
 * タブ行の「公開状態」欄。公開中の revision と、それと異なる最新の revision（公開待ち・処理中・失敗）を
 * 1 行で添える。最新が公開中と同じなら補足は出さない。
 */
export function TabPublishStatus({
  tab,
  onPreviewRequest,
  onHistoryRequest,
}: TabPublishStatusProps) {
  const latest = tab.latestRevision;
  const pending = latest !== null && latest.revision !== tab.activeRevision ? latest : null;

  return (
    <div>
      {tab.activeRevision === null ? (
        <StatusChip tone="neutral">未公開</StatusChip>
      ) : (
        <StatusChip tone="success">公開中 r{tab.activeRevision}</StatusChip>
      )}
      {pending?.status === 'ready' && (
        <StatusNote tone="warning">
          <CircleArrowUpIcon aria-hidden />r{pending.revision} が未公開 ·{' '}
          <InlineLinkButton
            aria-label={`${tab.name}の r${pending.revision} をプレビュー`}
            onClick={() => onPreviewRequest(pending.revision)}
          >
            プレビュー
          </InlineLinkButton>
        </StatusNote>
      )}
      {pending?.status === 'failed' && (
        <StatusNote tone="danger">
          <CircleXIcon aria-hidden />r{pending.revision} の処理に失敗 ·{' '}
          <InlineLinkButton aria-label={`${tab.name}の履歴を開く`} onClick={onHistoryRequest}>
            履歴
          </InlineLinkButton>
        </StatusNote>
      )}
      {pending !== null && pending.status !== 'ready' && pending.status !== 'failed' && (
        <StatusNote>
          <Loader2Icon aria-hidden className="animate-spin" />r{pending.revision} を処理中
        </StatusNote>
      )}
    </div>
  );
}
